// ─────────────────────────────────────────────────────────────────────────────
// MarketShare.jsx
// Market Share Dashboard page
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { RefreshCw } from "lucide-react";
import { CalibrationCard } from "../components/CalibrationCard";
import { PriceCorrelation } from "../components/PriceCorrelation";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Category colors
const categoryColors = {
  "Mixer Grinder": "#1F3864",
  "Mixer_Grinder": "#1F3864",
  "Pressure Cooker": "#375623",
  "Pressure_Cooker": "#375623",
  "Wet Grinder": "#7030A0",
  "Wet_Grinder": "#7030A0",
  "Gas Stove": "#C55A11",
  "Gas_Stove": "#C55A11",
};

// ─── Market Share Dashboard ───────────────────────────────────────────────────
export default function MarketShare() {
  const { isAdmin } = useOutletContext() || {};
  const [current, setCurrent] = useState({});
  const [trend, setTrend] = useState({});
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [calibKey, setCalibKey] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [curRes, trendRes] = await Promise.all([
        axios.get(`${API}/market-share/current`),
        axios.get(`${API}/market-share/trend?weeks=12`),
      ]);
      setCurrent(curRes.data.categories || curRes.data || {});
      setTrend(trendRes.data.categories || {});
    } catch (error) {
      console.error("Error fetching market share data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/market-share/sync`);
      toast.success("Market share data synced successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to sync market share data");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Convert current data to array if it's an object
  const currentArray = Array.isArray(current) 
    ? current 
    : Object.entries(current).map(([cat, data]) => ({
        category: cat,
        ...data
      }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1814]">Market Share</h1>
          <p className="text-sm text-[#8a8278]">
            Estimated market share based on BSR and sales velocity
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-[#1a1814] hover:bg-[#2a2824]"
        >
          <RefreshCw className={cn("h-4 w-4 mr-2", syncing && "animate-spin")} />
          {syncing ? "Syncing..." : "Sync Market Share"}
        </Button>
      </div>

      {/* Sales Calibration */}
      <CalibrationCard isAdmin={isAdmin} onCalibrationChange={() => { fetchData(); setCalibKey((k) => k + 1); }} />

      {/* Sales vs Price Correlation */}
      <PriceCorrelation categories={currentArray.map((d) => d.category).filter(Boolean)} refreshKey={calibKey} />

      {/* Market Share Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {currentArray.map((data) => {
          const cat = data.category;
          const displayName = cat?.replace("_", " ") || "Unknown";
          const color = categoryColors[cat] || categoryColors[displayName] || "#666";
          const marketSharePct = data.market_share_pct || data.own_share_by_rank || 0;
          
          return (
            <div key={cat} className="bg-white rounded-lg border p-4">
              <div className="text-sm text-gray-500 mb-1">
                {displayName}
              </div>
              <div
                className="text-3xl font-bold"
                style={{ color }}
              >
                {marketSharePct ? `${marketSharePct}%` : "—"}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Own market share (by units)
              </div>
              
              {/* Units breakdown */}
              {(data.own_estimated_units || data.own_products) && (
                <div className="text-xs text-gray-500 mt-1">
                  {data.own_estimated_units 
                    ? `~${data.own_estimated_units?.toLocaleString()} / ~${data.total_estimated_units?.toLocaleString()} units/mo`
                    : `${data.own_products} own / ${data.total_products} total`
                  }
                </div>
              )}
              
              {/* Progress bar */}
              <div className="mt-3">
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${marketSharePct}%`,
                      backgroundColor: color
                    }}
                  />
                </div>
              </div>
              
              {/* Top rankings */}
              {data.own_products_in_top_10 !== undefined && (
                <div className="grid grid-cols-2 gap-2 mt-3 text-center text-xs">
                  <div className="bg-blue-50 rounded p-1">
                    <span className="font-bold text-blue-700">{data.own_products_in_top_10}</span>
                    <span className="text-blue-600"> in Top 10</span>
                  </div>
                  <div className="bg-amber-50 rounded p-1">
                    <span className="font-bold text-amber-700">{data.competition_products_in_top_10}</span>
                    <span className="text-amber-600"> comp</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Market Share Trend Chart */}
      {Object.keys(trend).length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-semibold text-gray-800 mb-4">
            Market Share Trend — Last 12 Weeks
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="week"
                allowDuplicatedCategory={false}
                tick={{ fontSize: 10 }}
              />
              <YAxis
                tickFormatter={v => `${v}%`}
                domain={[0, 100]}
                tick={{ fontSize: 10 }}
              />
              <Tooltip formatter={(v) => `${v}%`} />
              <Legend />
              {Object.entries(trend).map(([cat, data]) => (
                <Line
                  key={cat}
                  data={data}
                  dataKey="market_share_pct"
                  name={cat.replace("_", " ")}
                  stroke={categoryColors[cat] || "#666"}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 mt-2">
            * Based on BSR → estimated units conversion for top 50 ASINs per category.
            Recalculated weekly every Sunday.
          </p>
        </div>
      )}

      {/* BSR Comparison by Category */}
      <div className="bg-white rounded-lg border p-4">
        <h3 className="font-semibold text-gray-800 mb-4">
          Category BSR Comparison
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {currentArray.map((data) => {
            const cat = data.category;
            const displayName = cat?.replace("_", " ") || "Unknown";
            
            return (
              <div key={cat} className="border rounded-lg p-3">
                <h4 className="font-medium text-sm mb-2">{displayName}</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="bg-blue-50 rounded p-2 text-center">
                    <p className="text-blue-600">Own Avg BSR</p>
                    <p className="font-bold text-blue-800">
                      {data.own_avg_bsr ? `#${data.own_avg_bsr.toLocaleString()}` : "—"}
                    </p>
                  </div>
                  <div className="bg-amber-50 rounded p-2 text-center">
                    <p className="text-amber-600">Comp Avg BSR</p>
                    <p className="font-bold text-amber-800">
                      {data.competition_avg_bsr ? `#${data.competition_avg_bsr.toLocaleString()}` : "—"}
                    </p>
                  </div>
                  <div className="bg-green-50 rounded p-2 text-center">
                    <p className="text-green-600">Own Best BSR</p>
                    <p className="font-bold text-green-800">
                      {data.own_best_bsr ? `#${data.own_best_bsr.toLocaleString()}` : "—"}
                    </p>
                  </div>
                  <div className="bg-red-50 rounded p-2 text-center">
                    <p className="text-red-600">Comp Best BSR</p>
                    <p className="font-bold text-red-800">
                      {data.competition_best_bsr ? `#${data.competition_best_bsr.toLocaleString()}` : "—"}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-800">
        <strong>✅ Calibrated with Helium 10 Data (Oct 2025):</strong> Market share is calculated using BSR → units
        conversion curves calibrated from 300 actual product sales across Gas Stove, Pressure Cooker, Wet Grinder,
        and Mixer Grinder categories. Uses linear interpolation between data points for improved accuracy.
        Figures are estimates and should be used for directional analysis.
      </div>
    </div>
  );
}
